"use client"

import ToggleButtons from "components/Buttons/ToggleButtons"
import Kicker from "components/Kicker"
import employee1 from "images/home/advantage/employeeM1.png"
import employee2 from "images/home/advantage/employeeM2.png"
import employee3 from "images/home/advantage/employeeM3.png"
import employee4 from "images/home/advantage/employeeM4.png"
import employee5 from "images/home/advantage/employeeM5.png"
import employer1 from "images/home/advantage/employersM1.png"
import employer2 from "images/home/advantage/employersM2.png"
import employer3 from "images/home/advantage/employersM3.png"
import employer4 from "images/home/advantage/employersM4.png"
import employer5 from "images/home/advantage/employersM5.png"
import { css, f, fresponsive, styled, unresponsive } from "library/styled"
import { useMedia } from "library/useMedia"
import { useEffect, useMemo, useRef, useState } from "react"
import colors from "styles/colors"
import textStyles from "styles/text"
import links from "utils/links"
import AdvantageContent from "./AdvantageContent"

const WILL_CHANGE_TRANSFORM = css`
	will-change: transform;
`

const ALL_EMPLOYER = [
	{
		heading: "Concierge Support, Zero HR Headaches",
		text: "Arlo’s 24/7 concierge app fields benefit questions, books cost-efficient care, and delivers $0 virtual urgent-care visits—keeping employees healthy and your HR inbox quiet.",
		image: employer3.src,
		number: "01",
	},
	{
		heading: "Predictable Pricing, Year After Year",
		text: "Avoid budget shocks with consistent, predictable renewal rates—thanks to Arlo’s investment in preventative care and better outcomes.",
		image: employer2.src,
		number: "02",
	},
	{
		heading: "Simple to Administer, Easy to Love",
		text: "Say goodbye to billing chaos and disconnected vendors. Arlo makes setup and ongoing management seamless.",
		image: employer1.src,
		number: "03",
	},
	{
		heading: "Fewer Hassles, More Health",
		text: "No more bouncing between authorizations, denied claims, or confusing paperwork. Employees focus on care—not red tape.",
		image: employer4.src,
		number: "04",
	},
	{
		heading: "Benefits Employees Will Actually Use",
		text: "Arlo offers comprehensive coverage and benefits that teams value—balancing affordability with real access to quality care.",
		image: employer5.src,
		number: "05",
		about: {
			text: "Learn More About Us",
			link: links.about,
		},
	},
]

export default function AdvantageMobile() {
	const wrapperRef = useRef<HTMLDivElement | null>(null)
	const topSectionRef = useRef<HTMLDivElement | null>(null)
	const [selectedOption, setSelectedOption] = useState(0)
	const alteredText = useMedia(
		"Care that\n Reaches Out",
		"Care that Reaches Out",
		"Care that\n Reaches Out",
		"Care that Reaches Out",
	)
	const [sectionHeight, setSectionHeight] = useState(0)

	const ALL_EMPLOYEE = useMemo(
		() => [
			{
				heading: "Zero-Cost Everyday Care",
				text: "Arlo plans include $0 virtual urgent-care visits and $0 in-person PCP appointments. Members can text a care team 24/7 or visit their preferred PCP—no bills, no surprise fees, no reason to delay care.",
				image: employee4.src,
				number: "01",
			},
			{
				heading: "Smarter Access,\nLess Stress",
				text: "Employees get a dedicated health guide through an easy to use mobile app—ready to help them navigate benefits, find high-quality providers, and schedule appointments.",
				image: employee1.src,
				number: "02",
			},
			{
				heading: "Personalized Concierge Support",
				text: "Concierge support cuts through the red tape—fewer prior authorizations, claim denials, and less time spent on hold. Employees can focus on getting care, not navigating systems.",
				image: employee2.src,
				number: "03",
			},
			{
				heading: alteredText,
				text: "The concierge proactively checks in when better care options are available—ensuring employees always get the highest value care with less effort.",
				image: employee3.src,
				number: "04",
			},
			{
				heading: "Affordable & Flexible",
				text: "Employees can choose care that fits their budget and their quality expectations—no compromises needed.",
				image: employee5.src,
				number: "05",
				about: {
					text: "Learn More About Us",
					link: links.about,
				},
			},
		],
		[alteredText],
	)

	useEffect(() => {
		if (topSectionRef.current) {
			const sectionHeight = topSectionRef.current.offsetHeight
			setSectionHeight(sectionHeight)
		}

		const handleResize = () => {
			if (topSectionRef.current) {
				const sectionHeight = topSectionRef.current.offsetHeight
				setSectionHeight(sectionHeight)
			}
		}

		window.addEventListener("resize", handleResize)
		return () => {
			window.removeEventListener("resize", handleResize)
		}
	}, [])

	return (
		<Wrapper ref={wrapperRef} sectionHeight={sectionHeight} data-header-hide>
			<PinnedWrapper>
				<Bottom>
					<ToggleButtons
						options={["Employees", "Employers"]}
						onChange={setSelectedOption}
					/>
				</Bottom>
			</PinnedWrapper>
			<TopSection ref={topSectionRef}>
				<Top>
					<StyledKicker doubleIcon size="large">
						Key Benefits
					</StyledKicker>
					<Title>The Arlo Advantage</Title>
				</Top>
				<Employees visible={selectedOption === 0}>
					{ALL_EMPLOYEE.map((item, i) => {
						return <AdvantageContent key={item.number} info={item} />
					})}
				</Employees>
				<Employers visible={selectedOption === 1}>
					{ALL_EMPLOYER.map((item, i) => {
						return <AdvantageContent reversed key={item.number} info={item} />
					})}
				</Employers>
			</TopSection>
		</Wrapper>
	)
}

const Wrapper = styled(
	"div",
	({ sectionHeight }: { sectionHeight: number }) => {
		return {
			...unresponsive(css`
				position: relative;
				height: ${sectionHeight}px;
			`),

			...fresponsive(css`
				width: 100%;
				padding: 0;
				will-change: transform;
				background: ${colors.greenMain};
				max-width: 375px;
				border-radius: 16px 16px 0 0;
				overflow: clip;
			`),
		}
	},
)

const PinnedWrapper = styled("div", {
	...fresponsive(css`
		position: sticky;
		width: 100%;
		height: 100dvh;
		padding: 0;
		margin: 0 auto;
		top: 0;
		bottom: 0;
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		align-items: center;
		${WILL_CHANGE_TRANSFORM}
		overflow: clip;
		z-index: 2;
		pointer-events: none;
	`),
})

const StyledKicker = styled(Kicker, {
	...fresponsive(css`
		color: ${colors.greenAccent};
	`),
})

const Title = styled("h1", {
	...f.mobile(css`
		${textStyles.h5};
		color: ${colors.white};
		text-align: center;
		display: block;
	`),
})

const Top = styled("div", {
	...fresponsive(css`
		position: relative;
		width: 100%;
		background: linear-gradient(0deg, #0e5e4900 0%, ${colors.greenMain} 66%);
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: start;
		gap: 24px;
		z-index: 3;
	`),

	...f.mobile(css`
		padding: 64px 0;
	`),
})

const Bottom = styled("div", {
	...fresponsive(css`
		position: relative;
		width: 100%;
		height: auto;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 24px;
		z-index: 3;
		background: linear-gradient(180deg, #0e5e4900 0%, ${colors.greenMain} 60%);
		padding: 40px 0 48px;
		pointer-events: auto;
	`),
})

const TopSection = styled("div", {
	...fresponsive(css`
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: auto;
		z-index: 1;
	`),
})

const Employers = styled("div", ({ visible }: { visible: boolean }) => ({
	...fresponsive(css`
		opacity: ${visible ? 1 : 0};
		transition: opacity 0.3s ease-in-out;
		position: absolute;
		pointer-events: ${visible ? "auto" : "none"};
		top: 270px;
		left: 0;
		width: 100%;
		height: auto;
	`),
}))

const Employees = styled("div", ({ visible }: { visible: boolean }) => ({
	...fresponsive(css`
		opacity: ${visible ? 1 : 0};
		pointer-events: ${visible ? "auto" : "none"};
		transition: opacity 0.3s ease-in-out;
		position: relative;
		width: 100%;
		height: auto;
	`),
}))
